// BookingManagement.tsx
// Admin Booking Management Component with full functionality

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  PencilIcon,
  EyeIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PlusIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  ChatBubbleBottomCenterTextIcon
} from '@heroicons/react/24/outline';
import { adminService, Booking as APIBooking } from '../../services/adminService';
import { exportToCSV, exportToExcel, exportToPDF, formatBookingsForExport } from '../../utils/exportUtils';
import { format } from 'date-fns';
import './BookingManagement.css';
import BookingEditDialog from './BookingEditDialog';
import BookingCancelDialog from './BookingCancelDialog';
import BookingNotesPanel from './BookingNotesPanel';
import BookingDetailsDialog from './BookingDetailsDialog';
import { CancellationForm, BookingNote as BookingNoteType, STATUS_TRANSITIONS } from '../../types/booking.types';
import './BookingEditDialog.css';

interface Booking {
  id: string;
  bookingNumber: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  tripDate: string;
  tripTime: string;
  tripType: string;
  participantCount: number;
  status: string; // Changed to string to support all backend statuses
  paymentStatus: 'pending' | 'partial' | 'paid' | 'refunded';
  totalAmount: number;
  paidAmount: number;
  notes: string[];
  createdAt: string;
  updatedAt: string;
  extras?: string[];
  specialRequests?: string;
  quoteValidUntil?: string; // Stichtag für Options
}

const BookingManagement: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSearchTerm = searchParams.get('search') || '';

  // Helper function for German currency formatting
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Helper function for German date and time formatting
  const formatDateTime = (date: string, time: string): string => {
    const formattedDate = new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    return `${formattedDate} ${time}`;
  };

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paymentFilter, setPaymentFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('bookingNumber');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    pendingPayment: 0,  // Buchungen mit ausstehender Zahlung
    confirmed: 0,       // Bestätigte Buchungen
    cancelled: 0,       // Stornierte Buchungen
    paidRevenue: 0      // Nur tatsächlich bezahlte Beträge (ohne stornierte)
  });

  // Export handlers
  const handleExportCSV = () => {
    const formattedData = formatBookingsForExport(bookings);
    const filename = `buchungen_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    exportToCSV(formattedData, filename);
  };

  const handleExportExcel = async () => {
    try {
      const filename = `buchungen_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      await exportToExcel('/api/admin/bookings/export', {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        dateFrom: dateFilter || undefined,
      }, filename);
    } catch (error) {
      console.error('Excel export failed:', error);
      alert('Excel-Export fehlgeschlagen. Bitte versuchen Sie es später erneut.');
    }
  };

  const handleExportPDF = async () => {
    try {
      const filename = `buchungen_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      await exportToPDF('/api/admin/bookings/export', {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        dateFrom: dateFilter || undefined,
      }, filename);
    } catch (error) {
      console.error('PDF export failed:', error);
      alert('PDF-Export fehlgeschlagen. Bitte versuchen Sie es später erneut.');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdownId(null);
    };

    if (openDropdownId) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openDropdownId]);

  // Load bookings on mount
  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    setLoading(true);
    setError(null);
    try {
      // Real API call
      const params = {
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        dateFrom: dateFilter || undefined,
        page: 1,
        pageSize: 10000  // Alle Buchungen laden
      };
      
      const response = await adminService.getBookings(params);

      // Transform API data to component format
      // Backend gibt PascalCase zurück, Frontend erwartet camelCase
      const responseData: any = response;
      const bookingsData = responseData.data?.Bookings || responseData.data?.bookings || response.bookings || [];
      const transformedBookings: Booking[] = bookingsData.map((b: any) => ({
        id: (b.Id || b.id)?.toString() || '',
        bookingNumber: b.BookingNumber || b.bookingNumber || '',
        customerId: (b.CustomerId || b.customerId)?.toString() || '',
        customerName: b.CustomerName || b.customerName || 'Unbekannt',
        customerEmail: b.CustomerEmail || b.customerEmail || '',
        customerPhone: b.CustomerPhone || b.customerPhone || '',
        tripDate: b.TripDate ? new Date(b.TripDate).toISOString().split('T')[0] :
                 b.tripDate ? new Date(b.tripDate).toISOString().split('T')[0] : '',
        tripTime: b.TripDate ? new Date(b.TripDate).toTimeString().substring(0, 5) :
                 b.tripTime || '',
        tripType: b.TourTypeName || b.tourTypeName || b.TripType || b.tripType || b.Variant || b.variant || '',
        participantCount: b.ParticipantCount || b.participantCount || 0,
        status: b.Status || b.status || 'Draft', // Keep original case for proper display
        paymentStatus: b.PaymentStatus ? (b.PaymentStatus.toLowerCase() as any) :
                      b.paymentStatus ||
                      (b.FullyPaid || b.fullyPaid ? 'paid' :
                       b.DepositPaid || b.depositPaid ? 'partial' : 'pending'),
        totalAmount: b.TotalPrice || b.totalPrice || b.TotalAmount || b.totalAmount || 0,
        paidAmount: b.PaidAmount || b.paidAmount ||
                   (b.FullyPaid || b.fullyPaid ? (b.TotalPrice || b.totalPrice) :
                    b.DepositPaid || b.depositPaid ? (b.TotalPrice || b.totalPrice) * 0.2 : 0),
        notes: b.Notes ? [b.Notes] : b.notes || [],
        createdAt: b.CreatedAt || b.createdAt || '',
        updatedAt: b.UpdatedAt || b.updatedAt || b.CreatedAt || b.createdAt || '',
        extras: (b.Extras || b.extras || []).map((e: any) => e.ExtraType || e.extraType || e),
        specialRequests: b.SpecialRequests || b.specialRequests || b.Notes || b.notes || '',
        quoteValidUntil: b.QuoteValidUntil || b.quoteValidUntil || undefined // Stichtag für Options
      }));
      
      setBookings(transformedBookings);

      // Calculate statistics (case-insensitive comparison)
      const getStatusLower = (s: string) => (s || '').toLowerCase();
      const getPaymentStatusLower = (s: string) => (s || '').toLowerCase();

      // Nicht-stornierte Buchungen für Umsatzberechnung
      const nonCancelledBookings = transformedBookings.filter(b => getStatusLower(b.status) !== 'cancelled');

      const stats = {
        total: transformedBookings.length,
        // Buchungen mit ausstehender Zahlung (nicht storniert)
        pendingPayment: nonCancelledBookings.filter(b => getPaymentStatusLower(b.paymentStatus) === 'pending').length,
        // Bestätigte Buchungen
        confirmed: transformedBookings.filter(b => ['confirmed', 'paymentpending', 'partiallypaid', 'fullypaid', 'paid'].includes(getStatusLower(b.status))).length,
        // Stornierte Buchungen
        cancelled: transformedBookings.filter(b => getStatusLower(b.status) === 'cancelled').length,
        // Nur tatsächlich bezahlte Beträge (paidAmount) von nicht-stornierten Buchungen
        paidRevenue: nonCancelledBookings.reduce((sum, b) => sum + (b.paidAmount || 0), 0)
      };
      setStats(stats);
      
    } catch (error: any) {
      console.error('❌ Error fetching bookings:', error);
      setError('Fehler beim Laden der Buchungen. Bitte Backend prüfen.');

      // ❌ KEIN FALLBACK ZU MOCK-DATEN!
      setBookings([]);
      setStats({
        total: 0,
        pendingPayment: 0,
        confirmed: 0,
        cancelled: 0,
        paidRevenue: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (bookingList: Booking[]) => {
    const getStatusLower = (s: string) => (s || '').toLowerCase();
    const getPaymentStatusLower = (s: string) => (s || '').toLowerCase();
    const nonCancelledBookings = bookingList.filter(b => getStatusLower(b.status) !== 'cancelled');

    const stats = {
      total: bookingList.length,
      pendingPayment: nonCancelledBookings.filter(b => getPaymentStatusLower(b.paymentStatus) === 'pending').length,
      confirmed: bookingList.filter(b => ['confirmed', 'paymentpending', 'partiallypaid', 'fullypaid', 'paid'].includes(getStatusLower(b.status))).length,
      cancelled: bookingList.filter(b => getStatusLower(b.status) === 'cancelled').length,
      paidRevenue: nonCancelledBookings.reduce((sum, b) => sum + (b.paidAmount || 0), 0)
    };
    setStats(stats);
  };

  // Status-Änderung mit Backend PATCH /api/admin/bookings/{id}/status
  const handleStatusChange = async (bookingId: string, newStatus: string) => {
    const booking = bookings.find(b => b.id === bookingId);
    if (!booking) return;

    // Workflow-Validierung (Frontend)
    const allowedTransitions = STATUS_TRANSITIONS[booking.status as keyof typeof STATUS_TRANSITIONS];
    if (!allowedTransitions || !allowedTransitions.includes(newStatus as any)) {
      alert(`Status-Übergang ${booking.status} → ${newStatus} nicht erlaubt!`);
      return;
    }

    // Grund abfragen (Backend erwartet min 5 Zeichen)
    const reason = prompt(`Grund für Status-Änderung auf "${newStatus}":`);
    if (!reason || reason.trim().length < 5) {
      alert('Bitte geben Sie einen Grund ein (mindestens 5 Zeichen)');
      return;
    }

    try {
      // Backend API Call: PATCH /api/admin/bookings/{id}/status
      // Body: { newStatus, reason }
      const response = await adminService.changeBookingStatus(parseInt(bookingId), newStatus, reason.trim());

      // Update local state with response data
      setBookings(prev => prev.map(b =>
        b.id === bookingId ? {
          ...b,
          status: newStatus as any,
          updatedAt: new Date().toISOString()
        } : b
      ));

      // Reload bookings to ensure full consistency
      fetchBookings();

      alert(`Status erfolgreich auf "${newStatus}" geändert`);
    } catch (err: any) {
      console.error('Error updating booking status:', err);
      alert(err.response?.data?.message || 'Fehler beim Ändern des Status');
    }
  };

  const handleAddNote = async (note: Partial<BookingNoteType>) => {
    if (!selectedBooking) return;

    try {
      // Add note through API
      await adminService.addBookingNote(parseInt(selectedBooking.id), {
        text: note.text || '',
        type: note.type || 'internal',
        isReminder: note.isReminder || false,
        reminderDate: note.reminderDate
      });

      alert('Notiz erfolgreich hinzugefügt');
      // Reload bookings to ensure consistency
      fetchBookings();
    } catch (err) {
      console.error('Error adding note:', err);
      throw err;
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!selectedBooking) return;

    try {
      // Backend expects number for noteId
      await adminService.deleteBookingNote(parseInt(selectedBooking.id), parseInt(noteId));
      alert('Notiz erfolgreich gelöscht');
    } catch (err) {
      console.error('Error deleting note:', err);
      throw err;
    }
  };

  // Handle cancel booking
  const handleCancelBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowCancelDialog(true);
  };

  // Handle confirm cancellation
  const handleConfirmCancellation = async (cancellation: CancellationForm) => {
    if (!selectedBooking) return;

    try {
      await adminService.cancelBookingWithRefund(
        parseInt(selectedBooking.id),
        {
          reason: cancellation.reason,
          refundAmount: cancellation.refundAmount,
          refundMethod: cancellation.refundMethod,
          internalNote: cancellation.internalNote
        }
      );

      // Update local state
      setBookings(prev => prev.map(b =>
        b.id === selectedBooking.id
          ? { ...b, status: 'cancelled' as any, paymentStatus: cancellation.refundAmount > 0 ? 'refunded' as any : b.paymentStatus }
          : b
      ));

      // Reload bookings
      fetchBookings();

      alert('Buchung erfolgreich storniert');
      setShowCancelDialog(false);
      setSelectedBooking(null);
    } catch (err: any) {
      console.error('Error cancelling booking:', err);
      throw new Error(err.message || 'Fehler beim Stornieren der Buchung');
    }
  };

  const handleExportSingleBookingPDF = async (bookingId: string) => {
    try {
      // Generate PDF through report API
      const blob = await adminService.exportReport('booking', 'pdf', { bookingId });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `booking-${bookingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert(`PDF für Buchung ${bookingId} wurde heruntergeladen`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Fehler beim Generieren des PDFs');
    }
  };

  // Handle edit booking
  const handleEditBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowEditDialog(true);
  };

  // Handle save edited booking
  const handleSaveEditedBooking = (updatedBooking: Booking) => {
    setBookings(prev => prev.map(b => 
      b.id === updatedBooking.id ? updatedBooking : b
    ));
    calculateStats(bookings);
    fetchBookings(); // Refresh from server
  };

  // Filter bookings
  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = searchTerm === '' ||
      booking.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.bookingNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.customerEmail.toLowerCase().includes(searchTerm.toLowerCase());

    // Case-insensitive status comparison
    const bookingStatusLower = (booking.status || '').toLowerCase();
    const matchesStatus = statusFilter === 'all' || bookingStatusLower === statusFilter.toLowerCase();

    // Case-insensitive payment status comparison
    const bookingPaymentStatusLower = (booking.paymentStatus || '').toLowerCase();
    const matchesPayment = paymentFilter === 'all' || bookingPaymentStatusLower === paymentFilter.toLowerCase();

    const matchesDate = dateFilter === '' || booking.tripDate === dateFilter;

    return matchesSearch && matchesStatus && matchesPayment && matchesDate;
  });

  // Sort bookings
  const sortedBookings = [...filteredBookings].sort((a, b) => {
    let comparison = 0;

    switch(sortBy) {
      case 'bookingNumber':
        // Extract numeric part for proper sorting (e.g., "B260038" -> 260038)
        const numA = parseInt(a.bookingNumber.replace(/\D/g, '')) || 0;
        const numB = parseInt(b.bookingNumber.replace(/\D/g, '')) || 0;
        comparison = numA - numB;
        break;
      case 'customer':
        comparison = a.customerName.localeCompare(b.customerName);
        break;
      case 'tripDate':
        comparison = new Date(a.tripDate).getTime() - new Date(b.tripDate).getTime();
        break;
      case 'tripType':
        comparison = a.tripType.localeCompare(b.tripType);
        break;
      case 'participantCount':
        comparison = a.participantCount - b.participantCount;
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'paymentStatus':
        comparison = a.paymentStatus.localeCompare(b.paymentStatus);
        break;
      case 'totalAmount':
        comparison = a.totalAmount - b.totalAmount;
        break;
      case 'createdAt':
        comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
      case 'updatedAt':
        comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
        break;
      default:
        comparison = 0;
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Handle column header click for sorting
  const handleSort = (column: string) => {
    if (sortBy === column) {
      // Toggle sort order if clicking same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Get sort indicator for column header
  const getSortIndicator = (column: string) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? ' ▲' : ' ▼';
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'confirmed': return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'pending': return <ClockIcon className="w-5 h-5 text-yellow-500" />;
      case 'cancelled': return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'completed': return <CheckCircleIcon className="w-5 h-5 text-blue-500" />;
      default: return null;
    }
  };

  const getPaymentStatusClass = (status: string) => {
    switch(status) {
      case 'paid': return 'payment-paid';
      case 'partial': return 'payment-partial';
      case 'pending': return 'payment-pending';
      case 'refunded': return 'payment-refunded';
      default: return '';
    }
  };

  const getStatusClass = (status: string) => {
    const normalizedStatus = status?.toLowerCase() || '';
    switch(normalizedStatus) {
      case 'confirmed': return 'status-confirmed';
      case 'pending':
      case 'draft': return 'status-pending';
      case 'option': return 'status-option';
      case 'cancelled': return 'status-cancelled';
      case 'completed': return 'status-completed';
      case 'fullypaid':
      case 'paid': return 'status-paid';
      case 'paymentpending':
      case 'partiallypaid':
      case 'awaitingfinalpayment': return 'status-payment';
      case 'pendingapproval': return 'status-pending-approval';
      // Angebots-Status
      case 'offersent': return 'status-offer-sent';
      case 'offeraccepted': return 'status-offer-accepted';
      case 'offerdeclined': return 'status-offer-declined';
      case 'offerexpired': return 'status-offer-expired';
      default: return '';
    }
  };

  const getStatusLabel = (status: string) => {
    const normalizedStatus = status?.toLowerCase() || '';
    switch(normalizedStatus) {
      case 'draft': return 'Entwurf';
      case 'option': return 'Option';
      case 'confirmed': return 'Bestätigt';
      case 'paymentpending': return 'Zahlung ausstehend';
      case 'awaitingfinalpayment': return 'Warte auf Restzahlung';
      case 'partiallypaid': return 'Teilweise bezahlt';
      case 'fullypaid':
      case 'paid': return 'Vollständig bezahlt';
      case 'inprogress': return 'In Bearbeitung';
      case 'completed': return 'Abgeschlossen';
      case 'cancelled': return 'Storniert';
      case 'noshow': return 'Nicht erschienen';
      case 'pendingapproval': return 'Genehmigung ausstehend';
      // Angebots-Status
      case 'offersent': return 'Angebot versendet';
      case 'offeraccepted': return 'Angebot angenommen';
      case 'offerdeclined': return 'Angebot abgelehnt';
      case 'offerexpired': return 'Angebot abgelaufen';
      default: return status || 'Unbekannt';
    }
  };

  if (loading) {
    return (
      <div className="booking-management loading">
        <div className="spinner">
          <ArrowPathIcon className="w-12 h-12 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="booking-management error">
        <p className="error-message">{error}</p>
        <button onClick={fetchBookings} className="btn-retry">
          <ArrowPathIcon className="w-5 h-5" />
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div className="booking-management">
      {/* Header with Statistics */}
      <div className="booking-header">
        <div className="header-row">
          <h2>Buchungsverwaltung</h2>
          <button
            onClick={() => navigate('/admin/booking-new')}
            className="btn-new-booking"
          >
            <PlusIcon style={{ width: '20px', height: '20px' }} />
            Neue Buchung
          </button>
          <div className="stats-row">
          <div
            className={`stat-card ${statusFilter === 'all' && paymentFilter === 'all' ? 'stat-card-active' : ''}`}
            onClick={() => { setStatusFilter('all'); setPaymentFilter('all'); }}
            style={{ cursor: 'pointer' }}
          >
            <span className="stat-label">Gesamt</span>
            <span className="stat-value">{stats.total}</span>
          </div>
          <div
            className={`stat-card ${paymentFilter === 'pending' && statusFilter === 'all' ? 'stat-card-active' : ''}`}
            onClick={() => { setPaymentFilter('pending'); setStatusFilter('all'); }}
            style={{ cursor: 'pointer' }}
            title="Buchungen mit ausstehender Zahlung"
          >
            <span className="stat-label">Ausstehend</span>
            <span className="stat-value text-yellow">{stats.pendingPayment}</span>
          </div>
          <div
            className={`stat-card ${statusFilter === 'confirmed' && paymentFilter === 'all' ? 'stat-card-active' : ''}`}
            onClick={() => { setStatusFilter('confirmed'); setPaymentFilter('all'); }}
            style={{ cursor: 'pointer' }}
            title="Bestätigte Buchungen"
          >
            <span className="stat-label">Bestätigt</span>
            <span className="stat-value text-green">{stats.confirmed}</span>
          </div>
          <div
            className={`stat-card ${statusFilter === 'cancelled' && paymentFilter === 'all' ? 'stat-card-active' : ''}`}
            onClick={() => { setStatusFilter('cancelled'); setPaymentFilter('all'); }}
            style={{ cursor: 'pointer' }}
            title="Stornierte Buchungen"
          >
            <span className="stat-label">Storniert</span>
            <span className="stat-value text-red">{stats.cancelled}</span>
          </div>
          <div className="stat-card stat-card-revenue" style={{ cursor: 'default' }} title="Nur tatsächlich bezahlte Beträge">
            <span className="stat-label">Umsatz</span>
            <span className="stat-value text-blue">{formatCurrency(stats.paidRevenue)}</span>
          </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="booking-filters">
        <div className="filter-row">
          <div className="search-box">
            <MagnifyingGlassIcon className="w-5 h-5" />
            <input
              type="text"
              placeholder="Suche nach Kunde, Buchungsnummer, E-Mail..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">Alle Status</option>
            <option value="pending">Ausstehend</option>
            <option value="confirmed">Bestätigt</option>
            <option value="cancelled">Storniert</option>
            <option value="completed">Abgeschlossen</option>
          </select>
          
          <select 
            value={paymentFilter} 
            onChange={(e) => setPaymentFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">Alle Zahlungen</option>
            <option value="pending">Zahlung ausstehend</option>
            <option value="partial">Teilweise bezahlt</option>
            <option value="paid">Vollständig bezahlt</option>
            <option value="refunded">Erstattet</option>
          </select>
          
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="date-filter"
          />
          
          <button onClick={fetchBookings} className="btn-refresh">
            <ArrowPathIcon className="w-5 h-5" />
            Aktualisieren
          </button>

          {/* Export buttons */}
          <div className="export-buttons" style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
            <button onClick={handleExportCSV} className="btn-export btn-csv" title="Als CSV exportieren">
              <DocumentArrowDownIcon className="w-5 h-5" />
              CSV
            </button>
            <button onClick={handleExportExcel} className="btn-export btn-excel" title="Als Excel exportieren">
              <DocumentArrowDownIcon className="w-5 h-5" />
              Excel
            </button>
            <button onClick={handleExportPDF} className="btn-export btn-pdf" title="Als PDF exportieren">
              <DocumentArrowDownIcon className="w-5 h-5" />
              PDF
            </button>
          </div>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bookings-table-container">
        <table className="bookings-table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => handleSort('bookingNumber')}>
                Buchungsnr.{getSortIndicator('bookingNumber')}
              </th>
              <th className="sortable" onClick={() => handleSort('customer')}>
                Kunde{getSortIndicator('customer')}
              </th>
              <th className="sortable" onClick={() => handleSort('tripDate')}>
                Datum & Zeit{getSortIndicator('tripDate')}
              </th>
              <th className="sortable" onClick={() => handleSort('tripType')}>
                Tour{getSortIndicator('tripType')}
              </th>
              <th className="sortable" onClick={() => handleSort('participantCount')}>
                Personen{getSortIndicator('participantCount')}
              </th>
              <th className="sortable" onClick={() => handleSort('status')}>
                Status{getSortIndicator('status')}
              </th>
              <th className="sortable" onClick={() => handleSort('paymentStatus')}>
                Zahlung{getSortIndicator('paymentStatus')}
              </th>
              <th className="sortable" onClick={() => handleSort('totalAmount')}>
                Betrag{getSortIndicator('totalAmount')}
              </th>
              <th className="sortable" onClick={() => handleSort('createdAt')}>
                Erstellt{getSortIndicator('createdAt')}
              </th>
              <th className="sortable" onClick={() => handleSort('updatedAt')}>
                Aktualisiert{getSortIndicator('updatedAt')}
              </th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {sortedBookings.length === 0 ? (
              <tr>
                <td colSpan={11} className="no-data">
                  Keine Buchungen gefunden
                </td>
              </tr>
            ) : (
              sortedBookings.map(booking => (
                <tr key={booking.id}>
                  <td className="booking-number">{booking.bookingNumber}</td>
                  <td>
                    <div className="customer-info">
                      <span className="customer-name">{booking.customerName}</span>
                      <span className="customer-email">{booking.customerEmail}</span>
                    </div>
                  </td>
                  <td>
                    <div className="date-time">
                      <span>{formatDateTime(booking.tripDate, booking.tripTime)}</span>
                    </div>
                  </td>
                  <td>{booking.tripType}</td>
                  <td className="text-center">{booking.participantCount}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(booking.status)}`}>
                      {getStatusLabel(booking.status)}
                    </span>
                  </td>
                  <td>
                    <span className={`payment-badge ${getPaymentStatusClass(booking.paymentStatus)}`}>
                      {booking.paymentStatus === 'pending' && 'Ausstehend'}
                      {booking.paymentStatus === 'partial' && `${formatCurrency(booking.paidAmount)} / ${formatCurrency(booking.totalAmount)}`}
                      {booking.paymentStatus === 'paid' && 'Bezahlt'}
                      {booking.paymentStatus === 'refunded' && 'Erstattet'}
                    </span>
                  </td>
                  <td className="amount">{formatCurrency(booking.totalAmount)}</td>
                  <td className="date-cell">
                    {booking.createdAt ? (
                      <>
                        {new Date(booking.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        <span className="time-display">{new Date(booking.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                      </>
                    ) : '-'}
                  </td>
                  <td className="date-cell">
                    {booking.updatedAt ? (
                      <>
                        {new Date(booking.updatedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        <span className="time-display">{new Date(booking.updatedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                      </>
                    ) : '-'}
                  </td>
                  <td className={openDropdownId === booking.id ? 'action-cell-open' : ''}>
                    <div className={`action-menu ${openDropdownId === booking.id ? 'open' : ''}`}>
                      <button
                        className="btn-action-menu"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdownId(openDropdownId === booking.id ? null : booking.id);
                        }}
                        title="Aktionen"
                      >
                        <span>...</span>
                      </button>
                      {openDropdownId === booking.id && (
                        <div className="action-dropdown">
                          {/* Ansicht */}
                          <button
                            className="dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBooking(booking);
                              setShowDetails(true);
                              setOpenDropdownId(null);
                            }}
                          >
                            <EyeIcon className="w-4 h-4" />
                            <span>Details anzeigen</span>
                          </button>

                          {/* Bearbeiten */}
                          <button
                            className="dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditBooking(booking);
                              setOpenDropdownId(null);
                            }}
                            disabled={booking.status === 'cancelled' || booking.status === 'completed'}
                          >
                            <PencilIcon className="w-4 h-4" />
                            <span>Bearbeiten</span>
                          </button>

                          {/* Notizen */}
                          <button
                            className="dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBooking(booking);
                              setShowNotes(true);
                              setOpenDropdownId(null);
                            }}
                          >
                            <ChatBubbleBottomCenterTextIcon className="w-4 h-4" />
                            <span>Notizen</span>
                            {booking.notes.length > 0 && (
                              <span className="dropdown-badge">{booking.notes.length}</span>
                            )}
                          </button>

                          {/* PDF Export */}
                          <button
                            className="dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExportSingleBookingPDF(booking.id);
                              setOpenDropdownId(null);
                            }}
                          >
                            <DocumentArrowDownIcon className="w-4 h-4" />
                            <span>PDF exportieren</span>
                          </button>

                          <div className="dropdown-divider"></div>

                          {/* Status-Änderungen */}
                          <button
                            className="dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange(booking.id, 'confirmed');
                              setOpenDropdownId(null);
                            }}
                            disabled={booking.status === 'confirmed'}
                          >
                            <CheckCircleIcon className="w-4 h-4" />
                            <span>Als bestätigt markieren</span>
                          </button>

                          <button
                            className="dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange(booking.id, 'completed');
                              setOpenDropdownId(null);
                            }}
                            disabled={booking.status === 'completed' || booking.status === 'cancelled'}
                          >
                            <ClockIcon className="w-4 h-4" />
                            <span>Als abgeschlossen markieren</span>
                          </button>

                          <div className="dropdown-divider"></div>

                          {/* Stornieren - gefährliche Aktion */}
                          <button
                            className="dropdown-item dropdown-item-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelBooking(booking);
                              setOpenDropdownId(null);
                            }}
                            disabled={booking.status === 'cancelled' || booking.status === 'completed'}
                          >
                            <TrashIcon className="w-4 h-4" />
                            <span>Stornieren</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Booking Details Dialog */}
      <BookingDetailsDialog
        booking={selectedBooking}
        isOpen={showDetails}
        onClose={() => {
          setShowDetails(false);
          setSelectedBooking(null);
        }}
        onEdit={() => {
          setShowDetails(false);
          if (selectedBooking) {
            handleEditBooking(selectedBooking);
          }
        }}
        onShowNotes={() => {
          setShowDetails(false);
          setShowNotes(true);
        }}
      />

      {/* Notes Panel - NEW COMPONENT */}
      <BookingNotesPanel
        booking={selectedBooking ? {
          ...selectedBooking,
          tripType: selectedBooking.tripType as any, // Type coercion
          status: selectedBooking.status as any // Type coercion for extended status
        } : null}
        isOpen={showNotes}
        onClose={() => setShowNotes(false)}
        onAddNote={handleAddNote}
        onDeleteNote={handleDeleteNote}
      />

      {/* Booking Edit Dialog */}
      {showEditDialog && selectedBooking && (
        <BookingEditDialog
          booking={selectedBooking}
          isOpen={showEditDialog}
          onClose={() => setShowEditDialog(false)}
          onSave={handleSaveEditedBooking}
        />
      )}

      {/* Booking Cancel Dialog */}
      <BookingCancelDialog
        booking={selectedBooking}
        isOpen={showCancelDialog}
        onClose={() => {
          setShowCancelDialog(false);
          setSelectedBooking(null);
        }}
        onConfirm={handleConfirmCancellation}
      />
    </div>
  );
};

export default BookingManagement;
